import AuthAPI from '../api/auth';
import BaseActionCableConnector from '../../shared/helpers/BaseActionCableConnector';
import DashboardAudioNotificationHelper from './AudioAlerts/DashboardAudioNotificationHelper';

class ActionCableConnector extends BaseActionCableConnector {
  constructor(app, pubsubToken) {
    const { websocketURL = '' } = window.hooryConfig || {};
    super(app, pubsubToken, websocketURL);
    this.CancelTyping = [];
    this.pickupData = null;
    this.events = {
      'message.created': this.onMessageCreated,
      'message.updated': this.onMessageUpdated,
      'conversation.created': this.onConversationCreated,
      'conversation.status_changed': this.onStatusChange,
      'user:logout': this.onLogout,
      'page:reload': this.onReload,
      'assignee.changed': this.onAssigneeChanged,
      'conversation.typing_on': this.onTypingOn,
      'conversation.typing_off': this.onTypingOff,
      'conversation.contact_changed': this.onConversationContactChange,
      'presence.update': this.onPresenceUpdate,
      'contact.deleted': this.onContactDelete,
      'contact.updated': this.onContactUpdate,
      'conversation.mentioned': this.onConversationMentioned,
      'notification.created': this.onNotificationCreated,
      'first.reply.created': this.onFirstReplyCreated,
      'conversation.read': this.onConversationRead,
      'conversation.updated': this.onConversationUpdated,
      'account.cache_invalidated': this.onCacheInvalidate,
      'conversation.pick_up.request': this.onChatPickUp,
    };
  }

  onReconnect = () => {
    this.syncActiveConversationMessages();
  };

  onDisconnected = () => {
    this.setActiveConversationLastMessageId();
  };

  setActiveConversationLastMessageId = () => {
    const {
      params: { conversation_id },
    } = this.app.$route;
    if (conversation_id) {
      this.app.$store.dispatch('setConversationLastMessageId', {
        conversationId: Number(conversation_id),
      });
    }
  };

  syncActiveConversationMessages = () => {
    const {
      params: { conversation_id },
    } = this.app.$route;
    if (conversation_id) {
      this.app.$store.dispatch('syncActiveConversationMessages', {
        conversationId: Number(conversation_id),
      });
    }
  };

  isAValidEvent = data => {
    return this.app.$store.getters.getCurrentAccountId === data.account_id;
  };

  onMessageUpdated = data => {
    if (data.content === 'This message was deleted') {
      this.app.$store.dispatch('stickyBanner/removeAudioRecords', data.id);
    }
    this.app.$store.dispatch('updateMessage', data);
  };

  onPresenceUpdate = data => {
    this.app.$store.dispatch('contacts/updatePresence', data.contacts);
    this.app.$store.dispatch('agents/updatePresence', data.users);
    this.app.$store.dispatch('setCurrentUserAvailability', data.users);
  };

  onConversationContactChange = payload => {
    const { meta = {}, id: conversationId } = payload;
    const { sender } = meta || {};
    if (conversationId) {
      this.app.$store.dispatch('updateConversationContact', {
        conversationId,
        ...sender,
      });
    }
  };

  onAssigneeChanged = payload => {
    const { id } = payload;
    if (id) {
      const audioNotificationStatus = this.app.$store.getters
        .getAudioNotificationStatus;
      const audioNotificationUnreadAssignedStatus = this.app.$store.getters
        .audioNotificationUnreadAssignedStatus;
      if (payload?.meta?.assignee?.id) {
        const alertAudioNotificationStatus = this.pickupData?.assignee_id
          ? 'none'
          : audioNotificationStatus;
        DashboardAudioNotificationHelper.onChangedAssignedAgent(
          alertAudioNotificationStatus,
          payload?.meta?.assignee?.id,
          audioNotificationUnreadAssignedStatus
        );
      }
      if (this.pickupData) {
        if (
          this.pickupData.conversation_id === id &&
          this.pickupData?.assignee_id !== payload?.meta?.assignee?.id
        ) {
          this.app.$root.$toast.close({ id: this.pickupData?.toastId });
          this.pickupData = null;
        }
      }

      this.app.$store.dispatch('updateConversation', payload);
    }
    this.fetchConversationStats();
  };

  onConversationCreated = data => {
    this.app.$store.dispatch('addConversation', data);
    this.fetchConversationStats();
    if (data.channel === 'Channel::Call') {
      if (data.custom_attributes.initiator === 'agent') {
        this.app.$store.dispatch('call/updateOutgoingCall', data);
      } else {
        this.app.$store.dispatch('call/newCall', data);
      }
    }
  };

  onConversationRead = data => {
    this.app.$store.dispatch('updateConversation', data);
  };

  onLogout = () => AuthAPI.logout();

  onMessageCreated = data => {
    if (data.attachments && data.attachments.length) {
      this.app.$store.dispatch('stickyBanner/addAudioRecords', data);
    }
    const {
      conversation: { last_activity_at: lastActivityAt },
      conversation_id: conversationId,
    } = data;

    DashboardAudioNotificationHelper.onNewMessage(data);
    this.app.$store.dispatch('addMessage', data);
    this.app.$store.dispatch('updateConversationLastActivity', {
      lastActivityAt,
      conversationId,
    });
  };

  onReload = () => window.location.reload();

  onStatusChange = data => {
    this.app.$store.dispatch('updateConversation', data);
    this.fetchConversationStats();
  };

  onConversationUpdated = data => {
    this.app.$store.dispatch('updateConversation', data);
    this.fetchConversationStats();
    if (data.channel === 'Channel::Call' && data.status === 'resolved') {
      this.app.$store.dispatch('call/endCall', data);
    } else if (data.channel === 'Channel::Call' && data.status === 'open') {
      this.app.$store.dispatch('call/nextCall', data);
    } else {
      this.app.$store.dispatch('call/updateOutgoingCall', data);
    }
  };

  onTypingOn = ({ conversation, user }) => {
    const conversationId = conversation.id;

    this.clearTimer(conversationId);
    this.app.$store.dispatch('conversationTypingStatus/create', {
      conversationId,
      user,
    });
    this.initTimer({ conversation, user });
  };

  onTypingOff = ({ conversation, user }) => {
    const conversationId = conversation.id;

    this.clearTimer(conversationId);
    this.app.$store.dispatch('conversationTypingStatus/destroy', {
      conversationId,
      user,
    });
  };

  onConversationMentioned = data => {
    this.app.$store.dispatch('addMentions', data);
  };

  clearTimer = conversationId => {
    const timerEvent = this.CancelTyping[conversationId];

    if (timerEvent) {
      clearTimeout(timerEvent);
      this.CancelTyping[conversationId] = null;
    }
  };

  initTimer = ({ conversation, user }) => {
    const conversationId = conversation.id;
    // Turn off typing automatically after 30 seconds
    this.CancelTyping[conversationId] = setTimeout(() => {
      this.onTypingOff({ conversation, user });
    }, 30000);
  };

  fetchConversationStats = () => {
    const conditions = this.app.$store.getters.getConversationListConditions;
    const filters = this.app.$store.getters.getAppliedConversationFilters;
    bus.$emit('fetch_conversation_stats', { conditions, filters });
    bus.$emit('fetch_overview_reports');
  };

  onContactDelete = data => {
    this.app.$store.dispatch(
      'contacts/deleteContactThroughConversations',
      data.id
    );
    this.fetchConversationStats();
  };

  onContactUpdate = data => {
    this.app.$store.dispatch('contacts/updateContact', data);
  };

  onNotificationCreated = data => {
    if (data.notification.notification_type === 'missed_call') {
      this.app.$store.dispatch('call/addMissedCall', data);
      this.app.$store.dispatch('setConversationsCallStatus', {
        status: 'missed_call',
        conversationId: data.notification.primary_actor.id,
      });
    } else {
      this.app.$store.dispatch('notifications/addNotification', data);
    }
  };

  onFirstReplyCreated = () => {
    bus.$emit('fetch_overview_reports');
  };

  onCacheInvalidate = data => {
    const keys = data.cache_keys;
    this.app.$store.dispatch('labels/revalidate', { newKey: keys.label });
    this.app.$store.dispatch('inboxes/revalidate', { newKey: keys.inbox });
    this.app.$store.dispatch('teams/revalidate', { newKey: keys.team });
  };

  onAllowedChatPickUp = data => {
    this.pickupData = { ...this.pickupData, toastId: data.toastId };
    this.app.$store.dispatch('assignAgent', {
      conversationId: data.conversation_id,
      agentId: data.assignee_id,
      source: 'chat_pickup',
    });
    this.app.$router.push({
      name: 'inbox_conversation',
      params: {
        conversation_id: data.conversation_id,
        accountId: data.account_id,
      },
    });
  };

  onRejectedChatPickUp = data => {
    this.app.$store.dispatch('rejectConversation', {
      conversationId: data.conversation_id,
      agentId: data.assignee_id,
    });
  };

  onChatPickUp = data => {
    this.pickupData = data;
    const accountInfo = this.app.$store.getters['accounts/getAccount'](
      data.account_id
    );
    const isAllowedReject =
      accountInfo.custom_attributes?.chat_pickup?.allow_reject || false;
    const audioNotificationStatus = this.app.$store.getters
      .getAudioNotificationStatus;
    DashboardAudioNotificationHelper.handleChatPickup(audioNotificationStatus);

    this.app.$root.$toast.show(
      'Incoming Chat',
      'default',
      30000,
      isAllowedReject,
      id => this.onAllowedChatPickUp({ ...data, toastId: id }),
      () => this.onRejectedChatPickUp(data),
      data,
      id => {
        this.pickupData = { ...this.pickupData, toastId: id };
      }
    );
  };
}

export default {
  init(pubsubToken) {
    return new ActionCableConnector(window.WOOT, pubsubToken);
  },
};
